// ILF Banner Slider — behaviour for sections/ilf-banner-slider.liquid.
//
// <ilf-banner-slider> owns one Swiper instance (plus an optional thumbnails
// instance) built from the JSON config that Liquid renders into the element's
// <script type="application/json" data-config> child. Swiper
// (swiper-bundle.min.js) and the selected effect module are loaded by the
// section itself with deferred script tags placed before this file, so both
// globals normally exist when the element upgrades; the element polls for
// them otherwise (deferred scripts on first load, scripts injected by a theme
// editor re-render after an effect switch) and leaves the CSS scroll-snap
// fallback in place when Swiper never arrives.
//
// What the element does:
// - promotes the wrapper Shopify puts around each theme block
//   (div.shopify-block) to the Swiper slide and fills the thumbnail strip
//   from the <template> each slide block renders; a wrapper whose block
//   rendered nothing (a slide without media, on the storefront) stays out of
//   the slider and loses its thumbnail, and the initial slide setting skips
//   to the next slide that rendered;
// - fits every thumbnail in the strip where thumbnails per view is 0 and
//   wires the strip's own arrows, which Swiper hides (locks) while every
//   thumbnail fits;
// - normalises the config per effect: single-slide effects force one slide
//   per view, carousel forces slidesPerView 'auto', centered effects force
//   centeredSlides; loop downgrades to rewind when there are too few slides;
// - runs the section's mobile effect (config.mobile, one of Swiper's
//   built-in effects) below 750px with the mobile layout, keeps the root
//   effect classes in step with the screen size, and rebuilds Swiper on the
//   same slide when the screen crosses 750px (Swiper cannot switch effects
//   through its breakpoints), clearing what the previous effect left behind;
// - initialises when the slider comes near the viewport (config.lazyInit),
//   immediately in the theme editor;
// - plays the video of each visible slide — the mobile or the desktop one
//   when a slide has both, following the screen size — and marks it
//   is-ready once it plays, so the stylesheet swaps it in over the image;
// - pauses autoplay and slide videos while the slider is off-screen and
//   resumes when it returns; honours reduced-motion / Save-Data when the
//   respect-reduced-motion attribute is present;
// - in the theme editor, slides to the block a merchant selects and holds
//   autoplay until it is deselected;
// - dispatches document CustomEvents ilf:banner-slider:init,
//   ilf:banner-slider:change ({ index, previousIndex, slide }) and
//   ilf:banner-slider:autoplay ({ state: 'start' | 'stop' }); every detail
//   carries { element, id }. element.swiper and element.thumbsSwiper expose
//   the instances; next(), prev() and goTo(index) are the public methods.

if (!customElements.get('ilf-banner-slider')) {
  const BREAKPOINT_TABLET = 750;
  const BREAKPOINT_DESKTOP = 990;
  const MOBILE_QUERY = `(max-width: ${BREAKPOINT_TABLET - 1}px)`;
  // The slide's own videos in its media layer (Material nests the media one
  // level deeper); videos of blocks in the content layer are not matched.
  const MEDIA_VIDEOS = '.slide-inner > .media > video, .swiper-material-content > .media > video';
  // What effects add to the slides and Swiper's destroy leaves in place:
  // shadows (built-in 3D effects), image clones and fragments (Shutters,
  // Slicer, Super Flow), and inline styles on the slide block's own
  // elements, which the section never renders with a style attribute.
  const EFFECT_ARTIFACTS =
    '[class*="swiper-slide-shadow"], .swiper-cube-shadow, .swiper-shutters-image-clones, .swiper-slicer-image-clones, .super-flow-fragments';
  const SLIDE_STRUCTURE =
    '.ilf-banner-slide, .slide-inner, .swiper-material-content, .media, .media > img, .media > picture > img, .media > video, .slide-link, .overlay, .content';
  const SINGLE_SLIDE_EFFECTS = new Set(['fade', 'cube', 'flip', 'cards', 'creative', 'shutters', 'slicer', 'super-flow']);
  const CENTERED_EFFECTS = new Set(['carousel', 'panorama', 'expo']);
  const CREATIVE_PRESETS = {
    stack: {
      prev: { shadow: true, translate: [0, 0, -400] },
      next: { translate: ['100%', 0, 0] },
    },
    depth: {
      prev: { translate: ['-120%', 0, -500] },
      next: { translate: ['120%', 0, -500] },
    },
    parallax: {
      prev: { translate: ['-20%', 0, -1] },
      next: { translate: ['100%', 0, 0] },
    },
    rotate: {
      prev: { shadow: true, translate: ['-125%', 0, -800], rotate: [0, 0, -90] },
      next: { shadow: true, translate: ['125%', 0, -800], rotate: [0, 0, 90] },
    },
    book: {
      prev: { shadow: true, origin: 'left center', translate: ['-5%', 0, -200], rotate: [0, 100, 0] },
      next: { origin: 'right center', translate: ['5%', 0, -200], rotate: [0, -100, 0] },
    },
  };
  const A11Y_MESSAGES = {
    prevSlideMessage: 'Previous slide',
    nextSlideMessage: 'Next slide',
    firstSlideMessage: 'This is the first slide',
    lastSlideMessage: 'This is the last slide',
    paginationBulletMessage: 'Go to slide {{index}}',
    slideLabelMessage: '{{index}} of {{slidesLength}}',
    containerRoleDescriptionMessage: 'carousel',
    itemRoleDescriptionMessage: 'slide',
  };

  // ---------------------------------------------------------------------
  // Services: pure helpers with no DOM side effects.
  // ---------------------------------------------------------------------

  const readConfig = (element) => {
    const script = element.querySelector(':scope > script[data-config]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('ilf-banner-slider: the config JSON could not be parsed.', error);
      return null;
    }
  };

  const prefersReducedMotion = () => {
    const query = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    return Boolean((query && query.matches) || (navigator.connection && navigator.connection.saveData));
  };

  const ceilSlides = (value) => (typeof value === 'number' ? Math.ceil(value) : 1);

  const numberOr = (value, fallback) => (typeof value === 'number' && !Number.isNaN(value) ? value : fallback);

  // A wrapper child holds a slide when its block rendered the slide root.
  const holdsSlide = (item) =>
    item.matches('.ilf-banner-slide') || Boolean(item.querySelector(':scope > .ilf-banner-slide'));

  // The initial slide setting counts every slide block (1-based); a block
  // that rendered nothing hands over to the next slide that did.
  const initialIndex = (items, slides, setting) => {
    const position = Math.min(Math.max(numberOr(setting, 1), 1), items.length) - 1;
    const index = slides.findIndex((slide) => items.indexOf(slide) >= position);
    return index === -1 ? slides.length - 1 : index;
  };

  // A slide with a desktop and a mobile video marks them video-desktop and
  // video-mobile; a single video plays at every screen size.
  const videoFits = (video, mobile) => {
    if (video.classList.contains('video-mobile')) return mobile;
    if (video.classList.contains('video-desktop')) return !mobile;
    return true;
  };

  // One { slidesPerView, spaceBetween } per breakpoint after the effect rules.
  const resolveLayouts = (breakpoints, effect) => {
    const layouts = {};
    ['mobile', 'tablet', 'desktop'].forEach((key) => {
      const raw = (breakpoints && breakpoints[key]) || {};
      let slidesPerView = numberOr(raw.slidesPerView, 1);
      let spaceBetween = numberOr(raw.spaceBetween, 0);
      if (SINGLE_SLIDE_EFFECTS.has(effect)) {
        slidesPerView = 1;
        spaceBetween = 0;
      }
      if (effect === 'carousel') {
        slidesPerView = 'auto';
        spaceBetween = 0;
      }
      layouts[key] = { slidesPerView, spaceBetween };
    });
    return layouts;
  };

  const maxSlidesPerView = (layouts) =>
    Math.max(...Object.values(layouts).map((layout) => ceilSlides(layout.slidesPerView)));

  // Slides needed before loop mode runs without blank or duplicated slides.
  const loopMinimum = (effect, layouts) => (effect === 'carousel' ? 5 : maxSlidesPerView(layouts) * 2);

  const groupFor = (slidesPerView, advance, loop, count) => {
    if (advance !== 'page' || typeof slidesPerView !== 'number') return 1;
    const group = Math.max(1, Math.floor(slidesPerView));
    if (loop && count % group !== 0) return 1;
    return group;
  };

  // Centered layouts always advance one slide: a group of several slides
  // snaps the initial position away from the first slide.
  const layoutParams = (layout, advance, loop, count, centered) => ({
    slidesPerView: layout.slidesPerView,
    slidesPerGroup: groupFor(layout.slidesPerView, centered ? 'single' : advance, loop, count),
    spaceBetween: layout.spaceBetween,
  });

  const resolveEffect = (config) => {
    const name = config.effect || 'slide';
    if (config.effectModule) {
      const module = window[config.effectModule];
      if (typeof module !== 'function') {
        console.warn(`ilf-banner-slider: effect module "${config.effectModule}" is not loaded; using the slide effect.`);
        return { name: 'slide', module: null, params: {} };
      }
      return { name, module, params: config.effectParams || {} };
    }
    if (name === 'creative') {
      const preset = CREATIVE_PRESETS[config.creativePreset] || CREATIVE_PRESETS.stack;
      return { name, module: null, params: { creativeEffect: preset } };
    }
    return { name, module: null, params: config.effectParams || {} };
  };

  // ---------------------------------------------------------------------
  // Controller: the custom element.
  // ---------------------------------------------------------------------

  class IlfBannerSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.config = readConfig(this);
      this.mainEl = this.querySelector('.swiper[data-role="main"]');
      if (!this.config || !this.mainEl) return;

      this.thumbsEl = this.querySelector('.swiper[data-role="thumbs"]');
      this.thumbsPrevEl = this.querySelector('.thumbs-prev');
      this.thumbsNextEl = this.querySelector('.thumbs-next');
      this.prevEl = this.querySelector('.nav-prev');
      this.nextEl = this.querySelector('.nav-next');
      this.paginationEl = this.querySelector('.pagination');
      this.designMode = this.hasAttribute('design-mode');
      this.reduceMotion = this.hasAttribute('respect-reduced-motion') && prefersReducedMotion();
      this.inViewport = true;
      this.pausedByViewport = false;
      this.editorHold = false;

      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.onVideoPlaying = this.onVideoPlaying.bind(this);
      this.onScreenChange = this.onScreenChange.bind(this);

      // One query serves the mobile videos and the mobile effect; the effect
      // classes follow it from the start (the inline script already did,
      // unless a theme editor re-render skipped it)
      if (window.matchMedia) {
        this.mobileQuery = window.matchMedia(MOBILE_QUERY);
        this.mobileQuery.addEventListener('change', this.onScreenChange);
      }
      this.applyEffectClasses();

      // A slider already within two viewports initialises at once; the
      // observer only serves the ones further down the page.
      const rect = this.getBoundingClientRect();
      const nearViewport = rect.bottom > -window.innerHeight && rect.top < window.innerHeight * 2;
      const lazy =
        this.config.lazyInit !== false && !this.designMode && !nearViewport && 'IntersectionObserver' in window;
      if (lazy) {
        this.initObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.initObserver.disconnect();
            this.initObserver = null;
            this.whenSwiperReady();
          },
          { rootMargin: '100% 0px' }
        );
        this.initObserver.observe(this);
      } else {
        this.whenSwiperReady();
      }
    }

    disconnectedCallback() {
      if (this.initObserver) this.initObserver.disconnect();
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      this.initObserver = null;
      this.visibilityObserver = null;
      if (this.readyTimer) clearTimeout(this.readyTimer);
      this.readyTimer = null;
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.removeEventListener('playing', this.onVideoPlaying, true);
      if (this.mobileQuery) this.mobileQuery.removeEventListener('change', this.onScreenChange);
      this.mobileQuery = null;
      if (this.swiper) this.swiper.destroy(true, true);
      if (this.thumbsSwiper) this.thumbsSwiper.destroy(true, true);
      this.swiper = null;
      this.thumbsSwiper = null;
      this.classList.remove('is-ready');
      this.initialised = false;
    }

    // --- initialisation ------------------------------------------------

    // Swiper and the effect module are deferred scripts on first load, and
    // scripts a theme editor re-render injects run after this element has
    // connected; both cases are covered by polling until the globals exist.
    // Once the page is complete, a missing module gives up after a few
    // seconds (init falls back to the slide effect) and a missing Swiper
    // leaves the CSS scroll-snap fallback in place.
    whenSwiperReady() {
      const moduleName = this.config.effectModule;
      const swiperReady = () => Boolean(window.Swiper);
      const moduleReady = () => !moduleName || typeof window[moduleName] === 'function';
      if (swiperReady() && moduleReady()) {
        this.init();
        return;
      }
      const started = Date.now();
      const poll = () => {
        this.readyTimer = null;
        if (!this.isConnected) return;
        if (swiperReady() && moduleReady()) {
          this.init();
          return;
        }
        const overdue = document.readyState === 'complete' && Date.now() - started > 8000;
        if (overdue) {
          if (swiperReady()) {
            this.init();
          } else {
            console.warn('ilf-banner-slider: Swiper is not loaded; the slider stays a scrollable row.');
          }
          return;
        }
        this.readyTimer = setTimeout(poll, 50);
      };
      this.readyTimer = setTimeout(poll, 50);
    }

    init() {
      if (this.swiper) return;
      const config = this.config;
      const { items, slides } = this.prepareSlides();
      this.slideCount = slides.length;
      if (this.slideCount === 0) return;

      if (this.thumbsEl && config.thumbs) this.fillThumbs(items);
      this.build(initialIndex(items, slides, config.initialSlide));
      this.classList.add('is-ready');

      if (this.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
      // Media events do not bubble; one capturing listener hears every video.
      this.addEventListener('playing', this.onVideoPlaying, true);
      this.observeVisibility();
      this.syncVideos();
      this.emit('init', { swiper: this.swiper, count: this.slideCount });
    }

    // Creates the main instance, and the thumbnail strip whose loop follows
    // it, for the effect in use at this screen size.
    build(initialSlide) {
      const config = this.config;
      const count = this.slideCount;
      const spec = this.activeSpec();
      const layouts = resolveLayouts(spec.breakpoints, spec.effect.name);
      const loop = Boolean(config.loop) && count >= loopMinimum(spec.effect.name, layouts);
      const rewind = Boolean(config.loop) && !loop;
      const advance = config.advance === 'single' ? 'single' : 'page';

      // The strip needs two slides, like the section's own rule; slides
      // that rendered nothing can leave fewer.
      if (this.thumbsEl && config.thumbs) {
        if (count > 1) {
          this.thumbsSwiper = new window.Swiper(this.thumbsEl, this.thumbsParams(config.thumbs, count, loop));
        } else {
          this.thumbsEl.closest('.thumbs').hidden = true;
        }
      }

      const params = this.mainParams(spec.effect, layouts, {
        loop,
        rewind,
        advance,
        count,
        initialSlide,
        centered: spec.centeredSlides,
      });
      this.lastIndex = params.initialSlide;
      this.swiper = new window.Swiper(this.mainEl, params);
      // Swiper's thumbs module can leave the highlight on the first thumbnail
      // when an instance is rebuilt on a later slide; it follows the running
      // slide once the instance exists.
      if (this.thumbsSwiper && this.swiper.thumbs) this.swiper.thumbs.update(true);
    }

    // Crossing 750px with a mobile effect: both instances go, whatever the
    // old effect left in the slides is cleared, and the slider starts again
    // on the same slide with the other effect. Autoplay stays held while the
    // slider is off-screen or a block is selected in the theme editor.
    rebuild() {
      const index = this.swiper.realIndex;
      this.swiper.destroy(true, true);
      if (this.thumbsSwiper) this.thumbsSwiper.destroy(true, true);
      this.swiper = null;
      this.thumbsSwiper = null;
      this.clearEffectArtifacts();
      this.build(index);
      const autoplay = this.swiper.autoplay;
      if (autoplay && autoplay.running && (!this.inViewport || this.editorHold)) {
        if (!this.inViewport) this.pausedByViewport = true;
        autoplay.stop();
      }
      this.emit('init', { swiper: this.swiper, count: this.slideCount });
    }

    clearEffectArtifacts() {
      this.mainEl.querySelectorAll(EFFECT_ARTIFACTS).forEach((element) => element.remove());
      this.mainEl.querySelectorAll(SLIDE_STRUCTURE).forEach((element) => element.removeAttribute('style'));
    }

    // --- screen size ------------------------------------------------------

    isMobile() {
      return Boolean(this.mobileQuery && this.mobileQuery.matches);
    }

    // Effect, layout and centering for the current screen size: the mobile
    // effect below 750px when the section sets one, the main effect
    // otherwise. The mobile layout applies at every Swiper breakpoint of that
    // instance, since crossing 750px rebuilds the slider.
    activeSpec() {
      const config = this.config;
      if (config.mobile && this.isMobile()) {
        const layout = (config.breakpoints && config.breakpoints.mobile) || {};
        return {
          effect: resolveEffect({ ...config.mobile, effectModule: '', creativePreset: config.creativePreset }),
          breakpoints: { mobile: layout, tablet: layout, desktop: layout },
          centeredSlides: config.mobile.centeredSlides,
        };
      }
      return { effect: resolveEffect(config), breakpoints: config.breakpoints, centeredSlides: config.centeredSlides };
    }

    // The markup carries the desktop effect's root classes and, with a mobile
    // effect, both sets as data attributes; the set of the effect in use is
    // applied (classes the two sets share stay).
    applyEffectClasses() {
      const mobileSet = (this.dataset.mobileEffectClasses || '').split(' ').filter(Boolean);
      if (mobileSet.length === 0) return;
      const desktopSet = (this.dataset.effectClasses || '').split(' ').filter(Boolean);
      const mobile = this.isMobile();
      this.classList.remove(...(mobile ? desktopSet : mobileSet));
      this.classList.add(...(mobile ? mobileSet : desktopSet));
    }

    // Shopify wraps each theme block in div.shopify-block, so the wrapper's
    // direct children are the slides; they get the swiper-slide class here.
    // items lists every child in block order, slides only those holding a
    // slide root (a slide without media renders nothing on the storefront).
    prepareSlides() {
      const wrapper = this.mainEl.querySelector('.swiper-wrapper');
      if (!wrapper) return { items: [], slides: [] };
      const items = Array.from(wrapper.children).filter(
        (element) => !['STYLE', 'SCRIPT', 'TEMPLATE'].includes(element.tagName)
      );
      const slides = items.filter(holdsSlide);
      slides.forEach((slide) => slide.classList.add('swiper-slide'));
      return { items, slides };
    }

    // Each slide block renders its thumbnail into a <template>; the strip has
    // one box per slide block, so each box is filled from its block's
    // template before Swiper reorders, and the box of a block that rendered
    // nothing is removed. Labels count the slides that remain.
    fillThumbs(items) {
      const thumbs = Array.from(this.thumbsEl.querySelectorAll('.swiper-wrapper > .swiper-slide'));
      let position = 0;
      items.forEach((item, index) => {
        const thumb = thumbs[index];
        if (!thumb) return;
        if (!holdsSlide(item)) {
          thumb.remove();
          return;
        }
        position += 1;
        const button = thumb.querySelector('.thumb-button');
        if (button) button.setAttribute('aria-label', `Show slide ${position}`);
        const source = item.querySelector('template.thumb-source');
        const target = thumb.querySelector('.thumb-button > .media');
        if (source && target && !target.hasChildNodes()) target.appendChild(source.content.cloneNode(true));
      });
    }

    mainParams(effect, layouts, state) {
      const config = this.config;
      const { loop, rewind, advance, count, initialSlide } = state;
      const centered = CENTERED_EFFECTS.has(effect.name)
        ? true
        : Boolean(state.centered) && !SINGLE_SLIDE_EFFECTS.has(effect.name);
      const params = {
        effect: effect.name,
        ...effect.params,
        speed: numberOr(config.speed, 600),
        direction: config.direction === 'vertical' ? 'vertical' : 'horizontal',
        initialSlide,
        grabCursor: config.grabCursor !== false,
        loop,
        rewind,
        loopAddBlankSlides: false,
        loopAdditionalSlides: effect.name === 'carousel' ? 1 : 0,
        centeredSlides: centered,
        ...layoutParams(layouts.mobile, advance, loop, count, centered),
        breakpoints: {
          [BREAKPOINT_TABLET]: layoutParams(layouts.tablet, advance, loop, count, centered),
          [BREAKPOINT_DESKTOP]: layoutParams(layouts.desktop, advance, loop, count, centered),
        },
        watchOverflow: true,
        watchSlidesProgress: true,
        runCallbacksOnInit: false,
        lazyPreloadPrevNext: 1,
        keyboard: config.keyboard ? { enabled: true, onlyInViewport: true } : false,
        mousewheel: config.mousewheel ? { forceToAxis: true } : false,
        freeMode: config.freeMode ? { enabled: true, sticky: true } : false,
        autoplay: this.autoplayParams(loop || rewind),
        navigation:
          this.prevEl && this.nextEl ? { prevEl: this.prevEl, nextEl: this.nextEl, addIcons: false } : false,
        pagination:
          this.paginationEl && config.pagination
            ? {
                el: this.paginationEl,
                type: config.pagination.type || 'bullets',
                clickable: true,
                dynamicBullets: Boolean(config.pagination.dynamicBullets),
              }
            : false,
        a11y: {
          ...A11Y_MESSAGES,
          containerRole: 'region',
          containerMessage: config.ariaLabel || null,
        },
        on: {
          // Loop mode re-fires slideChange while it reorders slides; the
          // change event goes out once per real slide.
          slideChange: (swiper) => {
            this.syncVideos();
            if (swiper.realIndex === this.lastIndex) return;
            const previousIndex = this.lastIndex;
            this.lastIndex = swiper.realIndex;
            this.emit('change', {
              index: swiper.realIndex,
              previousIndex,
              slide: swiper.slides[swiper.activeIndex] || null,
            });
          },
          autoplayStart: () => this.emit('autoplay', { state: 'start' }),
          autoplayStop: () => this.emit('autoplay', { state: 'stop' }),
        },
      };
      if (effect.module) params.modules = [effect.module];
      if (this.thumbsSwiper) params.thumbs = { swiper: this.thumbsSwiper, multipleActiveThumbs: false };
      return params;
    }

    autoplayParams(endless) {
      const autoplay = this.config.autoplay;
      if (!autoplay || this.reduceMotion) return false;
      return {
        delay: numberOr(autoplay.delay, 5000),
        pauseOnMouseEnter: autoplay.pauseOnMouseEnter !== false,
        disableOnInteraction: Boolean(autoplay.disableOnInteraction),
        stopOnLastSlide: !endless,
      };
    }

    thumbsParams(thumbs, count, mainLoop) {
      const layouts = resolveLayouts(thumbs.breakpoints, 'slide');
      // 0 per view fits every thumbnail of the slides that rendered
      Object.values(layouts).forEach((layout) => {
        if (layout.slidesPerView <= 0) layout.slidesPerView = count;
      });
      const loop = mainLoop && count >= maxSlidesPerView(layouts) * 2;
      return {
        ...layoutParams(layouts.mobile, 'single', loop, count),
        breakpoints: {
          [BREAKPOINT_TABLET]: layoutParams(layouts.tablet, 'single', loop, count),
          [BREAKPOINT_DESKTOP]: layoutParams(layouts.desktop, 'single', loop, count),
        },
        loop,
        loopAddBlankSlides: false,
        freeMode: { enabled: true, sticky: false },
        watchSlidesProgress: true,
        watchOverflow: true,
        grabCursor: true,
        navigation:
          this.thumbsPrevEl && this.thumbsNextEl
            ? { prevEl: this.thumbsPrevEl, nextEl: this.thumbsNextEl, addIcons: false }
            : false,
        a11y: { enabled: false },
      };
    }

    // --- viewport -------------------------------------------------------

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          this.inViewport = entry.isIntersecting;
          if (this.inViewport) {
            this.onEnterViewport();
          } else {
            this.onLeaveViewport();
          }
        },
        { threshold: 0.1 }
      );
      this.visibilityObserver.observe(this);
    }

    onLeaveViewport() {
      if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running) {
        this.pausedByViewport = true;
        this.swiper.autoplay.stop();
      }
      this.pauseVideos();
    }

    onEnterViewport() {
      if (this.pausedByViewport && !this.editorHold && this.swiper && this.swiper.autoplay) {
        this.pausedByViewport = false;
        this.swiper.autoplay.start();
      }
      this.syncVideos();
    }

    // --- videos ---------------------------------------------------------

    // Plays the video that fits the screen size in each slide Swiper marks
    // visible and pauses every other slide video.
    syncVideos() {
      if (!this.swiper) return;
      const mobile = this.isMobile();
      this.mainEl.querySelectorAll('.swiper-slide').forEach((slide) => {
        const visible = slide.classList.contains('swiper-slide-visible') || slide.classList.contains('swiper-slide-active');
        slide.querySelectorAll(MEDIA_VIDEOS).forEach((video) => {
          if (visible && this.inViewport && !this.reduceMotion && videoFits(video, mobile)) {
            const playing = video.play();
            if (playing && playing.catch) playing.catch(() => {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      });
    }

    // The first frame is on screen: the video replaces the image under it.
    onVideoPlaying(event) {
      const video = event.target;
      if (video instanceof HTMLVideoElement && video.matches(MEDIA_VIDEOS)) video.classList.add('is-ready');
    }

    // Crossing 750px swaps a slide's desktop and mobile videos and, with a
    // mobile effect, the effect classes and the running effect.
    onScreenChange() {
      if (this.config.mobile) {
        this.applyEffectClasses();
        if (this.swiper) this.rebuild();
      }
      this.syncVideos();
    }

    pauseVideos() {
      this.mainEl.querySelectorAll('video').forEach((video) => {
        if (!video.paused) video.pause();
      });
    }

    // --- theme editor ---------------------------------------------------

    onBlockSelect(event) {
      const slide = event.target.closest('.swiper-slide');
      if (!slide || !this.mainEl.contains(slide) || !this.swiper) return;
      this.editorHold = true;
      if (this.swiper.autoplay) this.swiper.autoplay.stop();
      if (this.swiper.params.loop) {
        const index = parseInt(slide.dataset.swiperSlideIndex, 10);
        if (!Number.isNaN(index)) this.swiper.slideToLoop(index, 300);
      } else {
        this.swiper.slideTo(Array.from(slide.parentElement.children).indexOf(slide), 300);
      }
    }

    onBlockDeselect(event) {
      const slide = event.target.closest('.swiper-slide');
      if (!slide || !this.mainEl.contains(slide) || !this.swiper) return;
      this.editorHold = false;
      if (this.swiper.params.autoplay && this.swiper.autoplay && !this.pausedByViewport) {
        this.swiper.autoplay.start();
      }
    }

    // --- public API -----------------------------------------------------

    next() {
      if (this.swiper) this.swiper.slideNext();
    }

    prev() {
      if (this.swiper) this.swiper.slidePrev();
    }

    goTo(index) {
      if (!this.swiper) return;
      if (this.swiper.params.loop) {
        this.swiper.slideToLoop(index);
      } else {
        this.swiper.slideTo(index);
      }
    }

    emit(name, detail) {
      document.dispatchEvent(
        new CustomEvent(`ilf:banner-slider:${name}`, {
          detail: { element: this, id: this.dataset.sectionId || null, ...detail },
        })
      );
    }
  }

  customElements.define('ilf-banner-slider', IlfBannerSlider);
}
